import { ADS_TRACER_API, DELETE, GET, POST, PUT } from '../constants';
import { jwtTokenHeader, request } from '../utils';
import { notification } from 'antd';

// TEST CASE FLOW
export const getTestCasesByUserId = async () =>
  request(
    GET,
    `${ADS_TRACER_API}/v1/users/test-cases`,
    {},
    await jwtTokenHeader(),
    'Fetching Test Cases failed, or none found.'
  );

export const createTestCase = async (reqBody) => {
  const errHandler = (err) => {
    notification.error({
      message: err.response?.data?.message ?? 'Creating test case failed',
      description: err.message,
      duration: 5,
    });
    throw err;
  };
  return request(
    POST,
    `${ADS_TRACER_API}/v1/users/test-cases`,
    reqBody,
    await jwtTokenHeader(),
    'Creating test case failed',
    true,
    errHandler
  );
};

export const updateTestCase = async (testCaseId) =>
  request(
    PUT,
    `${ADS_TRACER_API}/v1/users/test-cases/${testCaseId}`,
    {},
    await jwtTokenHeader(),
    `Updating Test Case ${testCaseId} failed.`
  );

export const deleteTestCase = async (testCaseId) => {
  const headers = await jwtTokenHeader({ 'Content-Type': 'application/json' });
  headers.data = {};
  return request(
    DELETE,
    `${ADS_TRACER_API}/v1/users/test-cases/${testCaseId}`,
    {},
    headers,
    `Deleting Test Case ${testCaseId} failed.`
  );
};

export const deleteSharedTestCase = async (testCaseId) => {
  const headers = await jwtTokenHeader({ 'Content-Type': 'application/json' });
  headers.data = {};
  return request(
    DELETE,
    `${ADS_TRACER_API}/v1/users/shared-test-cases/${testCaseId}`,
    {},
    headers,
    `Deleting Shared Test Case ${testCaseId} failed.`
  );
};

export const shareTestCase = async (testCaseId) =>
  request(
    PUT,
    `${ADS_TRACER_API}/v1/users/test-cases/${testCaseId}/share`,
    {},
    await jwtTokenHeader(),
    `Sharing Test Case: ${testCaseId} failed.`
  );

export const acceptSharedTestCase = async (testCaseId) =>
  request(
    PUT,
    `${ADS_TRACER_API}/v1/users/test-cases/${testCaseId}/accept`,
    {},
    await jwtTokenHeader(),
    `Accepting Shared Test Case: ${testCaseId} failed.`
  );

export const getTracesByUserIdAndTraceIds = async (testCaseId) =>
  request(
    GET,
    `${ADS_TRACER_API}/v1/traces/${testCaseId}`,
    {},
    await jwtTokenHeader(),
    `Fetching Trace for Test Case ${testCaseId} failed.`
  );

export const getLinkAccounts = async () => {
  const config = await jwtTokenHeader();
  config.data = {};
  return request(
    GET,
    `${ADS_TRACER_API}/v1/linked-accounts`,
    {},
    config,
    'Failed fetching Linked Accounts, or no accounts found'
  );
};

export const linkAccount = async (accountId, profileIds, nickname, publisherApp, channel, distroPartner, ipAddress) => {
  const errHandler = (err) => {
    const errorDetail = err.response?.data?.message || err.message || 'An error occurred while linking account';
    notification.error({
      message: 'Failed to link account',
      description: errorDetail,
      duration: 5,
    });
    return null; // Return null instead of throwing to prevent app crash
  };

  const payload = {
    'profile-ids': profileIds,
    nickname: nickname,
    'publisher-app': publisherApp,
    ...(ipAddress && { 'ip-address': ipAddress }),
    ...(accountId && { 'account-id': accountId }),
    ...(channel && { channel }),
    ...(distroPartner && { 'distro-partner': distroPartner }),
  };

  return request(
    POST,
    `${ADS_TRACER_API}/v1/accounts`,
    payload,
    await jwtTokenHeader(),
    'Failed to link account',
    false, // Disable default notification
    errHandler
  );
};

export const unlinkAccount = async (accountId) => {
  const errHandler = (err) => {
    const errorDetail = err.response?.data?.message || err.message || 'An error occurred while unlinking account';
    notification.error({
      message: `Failed to unlink account: ${accountId}`,
      description: errorDetail,
      duration: 5,
    });
    return null; // Return null instead of throwing to prevent app crash
  };

  const config = await jwtTokenHeader();
  config.data = {};
  return request(
    DELETE,
    `${ADS_TRACER_API}/v1/accounts/${accountId}`,
    {},
    config,
    `Failed to unlink the account: ${accountId}`,
    false, // Disable default notification
    errHandler
  );
};

export const linkNewsEntAccount = async (swid, nickname, app) =>
  request(
    POST,
    `${ADS_TRACER_API}/v1/accounts`,
    { 'account-id': swid, 'publisher-app': app, 'profile-ids': [], nickname: nickname },
    await jwtTokenHeader(),
    'Failed to link News and Entertainment account'
  );

export const linkRokuFastAccount = async (deviceIfa, nickname) => {
  const errHandler = (err) => {
    const errorDetail =
      err.response?.data?.message || err.message || 'An error occurred while linking Roku FAST account';
    notification.error({
      message: 'Failed to link Roku FAST account',
      description: errorDetail,
      duration: 5,
    });
    return null;
  };

  const payload = {
    'device-ifa': deviceIfa,
    nickname: nickname,
    'publisher-app': 'ROKU',
  };

  return request(
    POST,
    `${ADS_TRACER_API}/v1/accounts/roku`,
    payload,
    await jwtTokenHeader(),
    'Failed to link Roku FAST account',
    false,
    errHandler
  );
};

export const linkFuboAccount = async (fuboUserId, nickname) => {
  const errHandler = (err) => {
    const errorDetail = err.response?.data?.message || err.message || 'An error occurred while linking Fubo account';
    notification.error({
      message: 'Failed to link Fubo account',
      description: errorDetail,
      duration: 5,
    });
    return null;
  };

  const payload = {
    'fubo-user-id': fuboUserId,
    nickname,
    'publisher-app': 'FUBO_TV',
  };

  return request(
    POST,
    `${ADS_TRACER_API}/v1/accounts/fubo`,
    payload,
    await jwtTokenHeader(),
    'Failed to link Fubo account',
    false,
    errHandler
  );
};
